import { FC, useContext } from "react";
import Table from "../../../components/Table";
import Tooltip from "../../../components/Tooltip";
import { AuthContext } from "../../../contexts/AuthContext";
import { Invite } from "../../../types/api/invite";
import { getFormattedFrDate } from "../../../utils/date";
import { anonymize, anonymizeEmail } from "../../../utils/string";
import { CrossIcon, CopyLinkIcon, UpdateIcon } from "../../../assets/img/icons";

import styles from "./styles.module.scss";

type Props = {
    isLoading: boolean;
    invites: Invite[];
    rowActionLoading: Record<
        string,
        "resend" | "cancel" | "copyLink" | undefined
    >;
    onResendInvite: (email: string) => void;
    onCancelInvite: (email: string) => void;
    onCopyInviteLink: (email: string) => void;
};

const InvitesTable: FC<Props> = ({
    isLoading,
    invites,
    rowActionLoading,
    onResendInvite,
    onCancelInvite,
    onCopyInviteLink,
}) => {
    const { isStreamOn, hasPermission } = useContext(AuthContext)!;

    const getNameColumn = (invite: Invite) => {
        if (invite.discordName) {
            return isStreamOn
                ? anonymize(invite.discordName)
                : invite.discordName;
        } else {
            return isStreamOn ? anonymizeEmail(invite.email!) : invite.email!;
        }
    };

    const formatInvites = (invites: Invite[]) => {
        return invites.map((invite) => {
            const date = new Date(invite.createdAt);
            const normalizedStatus = String(invite.status || "").toLowerCase();
            const inviteEmail = invite.email ?? "";
            const canShowEmailActions =
                hasPermission("invite", "create") &&
                Boolean(inviteEmail) &&
                normalizedStatus !== "used";
            const canShowCopyLink =
                hasPermission("invite", "read") &&
                Boolean(inviteEmail) &&
                normalizedStatus === "unused";

            return {
                ...invite,
                discordName: (
                    <div className={styles.value}>{getNameColumn(invite)}</div>
                ),
                createdAt: (
                    <div className={styles.value}>{`${getFormattedFrDate(date)} à ${date.getHours()}h${
                        date.getMinutes() < 10 ? "0" : ""
                    }${date.getMinutes()}`}</div>
                ),
                status: (
                    <div className={styles.inviteStatusContainer}>
                        <div
                            className={
                                invite.status === "used"
                                    ? styles.inviteStatusUsed
                                    : styles.inviteStatusPending
                            }
                        />
                    </div>
                ),
                actions:
                    canShowEmailActions || canShowCopyLink ? (
                        <div className={styles.actionsCell}>
                            {canShowEmailActions && (
                                <>
                                    <Tooltip
                                        content="Renvoyer l’invitation"
                                        placement="top"
                                    >
                                        <button
                                            type="button"
                                            className={styles.actionButton}
                                            disabled={Boolean(
                                                rowActionLoading[inviteEmail]
                                            )}
                                            onClick={() =>
                                                onResendInvite(inviteEmail)
                                            }
                                        >
                                            {rowActionLoading[inviteEmail] ===
                                            "resend" ? (
                                                <span
                                                    className={styles.inlineSpinner}
                                                />
                                            ) : (
                                                <UpdateIcon
                                                    width={15}
                                                    height={15}
                                                />
                                            )}
                                        </button>
                                    </Tooltip>
                                    <Tooltip
                                        content="Annuler l’invitation"
                                        placement="top"
                                    >
                                        <button
                                            type="button"
                                            className={styles.actionButton}
                                            disabled={Boolean(
                                                rowActionLoading[inviteEmail]
                                            )}
                                            onClick={() =>
                                                onCancelInvite(inviteEmail)
                                            }
                                        >
                                            {rowActionLoading[inviteEmail] ===
                                            "cancel" ? (
                                                <span
                                                    className={styles.inlineSpinner}
                                                />
                                            ) : (
                                                <CrossIcon
                                                    width={13}
                                                    height={13}
                                                />
                                            )}
                                        </button>
                                    </Tooltip>
                                </>
                            )}

                            {canShowCopyLink && (
                                <Tooltip
                                    content="Copier le lien d'invitation"
                                    placement="top"
                                >
                                    <button
                                        type="button"
                                        className={styles.copyLinkButton}
                                        disabled={Boolean(
                                            rowActionLoading[inviteEmail]
                                        )}
                                        onClick={() => onCopyInviteLink(inviteEmail)}
                                    >
                                        {rowActionLoading[inviteEmail] ===
                                        "copyLink" ? (
                                            <span className={styles.inlineSpinner} />
                                        ) : (
                                            <CopyLinkIcon
                                                width={14}
                                                height={14}
                                            />
                                        )}
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    ) : (
                        <div className={styles.actionsCellEmpty} />
                    ),
            };
        });
    };

    return (
        <Table
            rows={[
                {
                    label: <div className={styles.column}>EMAIL / DISCORD</div>,
                    value: "discordName",
                    className: styles.column,
                },
                {
                    label: <div className={styles.column}>DATE</div>,
                    value: "createdAt",
                },
                { label: "", value: "status", width: 42 },
                {
                    label: "",
                    value: "actions",
                    width: 140,
                },
            ]}
            data={formatInvites(invites)}
            loading={isLoading}
        />
    );
};

export default InvitesTable;
